/**
 * PDP Media Gallery (AC-146, 2026-07-13) — Figs-parity autoplaying gallery video.
 * Loaded by snippets/pdp-media-gallery-content.liquid ONLY when the block
 * setting video_autoplay is on (root class media-gallery--video-autoplay).
 *
 * Figs benchmark behavior (FigsGalleryReferenceSpec-2026-07-13.md §5):
 * - <video autoplay loop muted playsinline preload="metadata">, no controls;
 * - plays while visible, pauses when scrolled/swiped out of view;
 * - a small circular play/pause toggle sits bottom-right of the media pane
 *   (the vendored .video-interaction-hint button, restyled in pdp-gallery.css).
 *
 * The <video> is rendered inline by snippets/video.liquid (video_autoplay: true);
 * this module patches the attributes Liquid cannot emit (playsinline/preload),
 * keeps the deferred-media component's isPlaying/hint state in sync, and adds
 * the IntersectionObserver play/pause. No behavior change for any gallery
 * whose video_autoplay setting is off (script not even loaded).
 */

function initGallery(gallery) {
  for (const dm of gallery.querySelectorAll('.product-media deferred-media[autoplay]')) {
    const video = dm.querySelector('video');
    if (!video || video.dataset.pdpAutoplayInit) continue;
    video.dataset.pdpAutoplayInit = 'true';

    // Figs video element attributes (spec §5).
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'metadata');
    video.removeAttribute('controls');

    // Keep the component's play/pause hint (the bottom-right circle) in sync
    // with the real element state, whatever triggers it.
    const sync = (isPlaying) => {
      dm.isPlaying = isPlaying;
      if (typeof dm.updatePlayPauseHint === 'function') dm.updatePlayPauseHint(isPlaying);
    };
    video.addEventListener('play', () => sync(true));
    video.addEventListener('pause', () => sync(false));
    sync(!video.paused);

    // Autoplay-on-visible / pause-when-offscreen (spec §9 recommendation).
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(dm);
  }
}

/**
 * AC-146 (2026-07-13) — Figs live video thumbnails (spec §3/§5): the rail tile
 * for a Shopify-hosted video is a real muted looping <video> rendered by
 * snippets/pdp-gallery-slideshow-controls.liquid ONLY when the block setting
 * thumbnail_video_live is on. The element ships with preload="none" and an
 * empty src; this attaches the (smallest-mp4) src lazily when the tile nears
 * the viewport, autoplays it muted, and pauses it offscreen — mirroring the
 * lazy ProgressiveAsset behavior Figs uses.
 * @param {ParentNode} root - The root to scan for live thumb videos.
 */
function initThumbVideos(root) {
  for (const video of root.querySelectorAll('video[data-pdp-thumb-video]')) {
    if (video.dataset.pdpThumbInit) continue;
    video.dataset.pdpThumbInit = 'true';

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) video.src = video.dataset.src;
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(video);
  }
}

for (const gallery of document.querySelectorAll('pdp-media-gallery.media-gallery--video-autoplay')) {
  initGallery(gallery);
}

initThumbVideos(document);
